/* eslint-disable react/no-danger */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Typography
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { Form, Formik, Field } from 'formik';
import * as Yup from 'yup';
import { nivelUsuarioEnum } from 'shared/Enums';
import { mascararCpfCnpj } from '../../../../shared';
import { getListarLimiteComercial, postSalvarLimiteComercial } from '../../../../api/dashboard';
import LoaderMain from '../../../Onboarding/shared/LoaderMain';
import ButtonMain from '../../../Onboarding/shared/ButtonMain';
import InputMain from '../../../Onboarding/shared/InputMain/InputMain';
import { unmaskValue } from '../../../../utils/format';
import indicesNomeProdutos from '../../../Onboarding/shared/Enums/EnumsIndicesNomeProdutos';
import './styles.scss';

const ModalGridDashboardLimiteComercial = ({
  modalGridLimiteComercial,
  setModalGridLimiteComercial,
  setAlerta,
  setListaClientes,
  dadosUser
}) => {
  const [isPending, setIsPending] = useState(false);
  const [dadosLimitesComerciais, setDadosLimitesComerciais] = useState([]);

  async function buscarLimitesComerciais() {
    if (modalGridLimiteComercial && modalGridLimiteComercial.cnpjCliente) {
      setIsPending(true);
      try {
        const res = await getListarLimiteComercial(modalGridLimiteComercial.params.id);
        const dados = res.data;
        setDadosLimitesComerciais(dados);
        return dados;
      } catch (err) {
        setAlerta({
          mensagem: 'Houve um erro ao buscar o limite comercial. Favor contatar o suporte.',
          tipo: 'error'
        });
        return null;
      } finally {
        setIsPending(false);
      }
    }
    return null;
  }

  useEffect(() => {
    buscarLimitesComerciais();
  }, [modalGridLimiteComercial]);

  const salvarLimiteComercial = async (form, params) => {
    setIsPending(true);
    try {
      const res = await postSalvarLimiteComercial(params);
      if (res.data) {
        setAlerta({
          mensagem: res.data.mensagem,
          tipo: res.data.error ? 'warning' : 'success'
        });

        const novosDados = await getListarLimiteComercial(modalGridLimiteComercial.params.id);

        if (novosDados.data) {
          setDadosLimitesComerciais({ ...novosDados.data });
        }
        form.setSubmitting(false);
      }
    } catch (error) {
      const mensagemError =
        error.response.data.mensagem || 'Houve um erro ao salvar o limite comercial.';
      if (error.response.data.error) {
        form.setSubmitting(false);
      } else {
        setModalGridLimiteComercial(false);
      }
      setAlerta({ mensagem: mensagemError, tipo: 'error' });
    }
  };

  const cadastrarLimiteComercial = async (values, form) => {
    setIsPending(true);

    if (modalGridLimiteComercial && modalGridLimiteComercial.cnpjCliente) {
      const limites = Object.keys(values)
        .filter(key => key !== 'observacao')
        .map(codProduto => ({
          codProduto,
          valorLimite: unmaskValue(values[codProduto]) || 0.0
        }));

      let params = {
        codCliente: modalGridLimiteComercial.params.id,
        observacao: values.observacao || '',
        limites
      };

      let limitesProduto = {
        limite_normal: 0,
        limite_comissaria: 0,
        limite_intercompany: 0,
        limite_giro_rapido: 0,
        limite_giro_parcelado: 0,
        limite_conta_escrow: 0,
        limite_escrow_simples: 0,
        limite_capital_flow_partner: 0
      };

      const camposAtualizados = {};

      limites.forEach(item => {
        const nomeProduto = indicesNomeProdutos[item.codProduto];
        const chave = `limite_${nomeProduto}`;

        if (chave in limitesProduto) {
          camposAtualizados[chave] = item.valorLimite;
        }
      });

      setListaClientes(prevState =>
        prevState.map(cliente => {
          if (cliente.cnpjCliente === modalGridLimiteComercial.cnpjCliente) {
            const updatedCliente = {
              ...cliente,
              ...camposAtualizados
            };

            if (cliente.infoProdutos) {
              const updatedInfoProdutos = { ...cliente.infoProdutos };
              limites.forEach(item => {
                if (updatedInfoProdutos[item.codProduto]) {
                  updatedInfoProdutos[item.codProduto].limite = item.valorLimite;
                }
              });
              updatedCliente.infoProdutos = updatedInfoProdutos;
            }
            return updatedCliente;
          }
          return cliente;
        })
      );

      await salvarLimiteComercial(form, params);
      form.setSubmitting(false);
      setIsPending(false);
    }
  };

  const initialValues =
    dadosLimitesComerciais && (dadosLimitesComerciais.produtos || dadosLimitesComerciais.observacao)
      ? Object.keys(dadosLimitesComerciais.produtos).reduce(
          (acc, key) => {
            const produto = dadosLimitesComerciais.produtos[key];
            acc[produto.codProduto] = produto.valorProduto || 0.0;
            return acc;
          },
          { observacao: dadosLimitesComerciais.observacao }
        )
      : {};

  const schemaFields = {
    observacao: Yup.string().required('O campo de observação é obrigatório')
  };

  if (
    dadosLimitesComerciais &&
    dadosLimitesComerciais.produtos &&
    dadosLimitesComerciais.produtos.length > 0
  ) {
    dadosLimitesComerciais.produtos.forEach(produto => {
      schemaFields[produto.codProduto] = Yup.number()
        .transform(function(value, originalValue) {
          return unmaskValue(originalValue);
        })
        .max(
          produto.valorLimiteMaximoProduto,
          `A meta excede o Limite Total do produto (Crédito: ${produto.valorLimiteMaximoProduto.toLocaleString(
            'pt-BR',
            {
              style: 'currency',
              currency: 'BRL'
            }
          )}).`
        );
    });
  }

  const validationSchema = Yup.object().shape(schemaFields);

  const InnerForm = ({ produtos, handleSubmit, isSubmitting, validateForm }) => {
    React.useEffect(() => {
      validateForm();
    }, []);

    return (
      <Form>
        <Grid container>
          <>
            {produtos ? (
              Object.keys(produtos).length > 0 ? (
                <Grid container spacing={2}>
                  {Object.keys(produtos)
                    .filter(key => key !== 'observacao')
                    .map(key => {
                      const limite = produtos[key];
                      return (
                        <Grid item xl={6} lg={6} md={6} sm={12} xs={12} key={limite.codProduto}>
                          <Field
                            type="text"
                            name={`${limite.codProduto}`}
                            titulo={limite.nomeProduto}
                            id={`${limite.codProduto}`}
                            size="small"
                            className="select-dashBoard"
                            component={InputMain}
                            tipoMascara="number-money"
                            autoComplete="off"
                          />
                        </Grid>
                      );
                    })}
                </Grid>
              ) : (
                <Box className="w-100">
                  <Alert severity="warning" className="w-100">
                    Não há produtos disponíveis para este cliente.
                  </Alert>
                </Box>
              )
            ) : null}

            <Grid container>
              <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
                <Field
                  titulo="Observação"
                  name="observacao"
                  placeholder="Observação"
                  id="observacao"
                  fullWidth
                  uppercase={false}
                  component={InputMain}
                  autoComplete="off"
                  multiline
                  rows={4}
                  inputProps={{ maxLength: 10000 }}
                />
              </Grid>
            </Grid>
          </>

          <Grid
            item
            xl={12}
            lg={12}
            md={12}
            sm={12}
            xs={12}
            container
            direction="row"
            className="items-direita items-centro-verticalmente"
          >
            <Box className="items-direita flex-box w-auto margin-top-15" gap={1.5}>
              <ButtonMain tipoBotao="transparente" onClick={() => setModalGridLimiteComercial(false)}>
                Cancelar
              </ButtonMain>
              <ButtonMain
                tipoBotao="azul-escuro"
                onClick={handleSubmit}
                disabled={
                  isPending ||
                  isSubmitting ||
                  dadosUser.nivel === nivelUsuarioEnum.GERENTE_NEGOCIOS ||
                  dadosUser.nivel === nivelUsuarioEnum.CLIENTE
                }
              >
                Salvar
              </ButtonMain>
            </Box>
          </Grid>
          {isSubmitting && <LinearProgress />}
        </Grid>
      </Form>
    );
  };

  return (
    <Dialog
      open={modalGridLimiteComercial !== false}
      fullWidth={true}
      maxWidth="md"
      keepMounted
      onClose={() => setModalGridLimiteComercial(false)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title" className="cabecalho-modal">
        <Box className="cabecalho-modal-titulo">
          {modalGridLimiteComercial ? modalGridLimiteComercial.modalTitle : ''}
          <IconButton
            onClick={() => setModalGridLimiteComercial(false)}
            className="float-right margin-neg-top-5"
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {modalGridLimiteComercial ? (
          <Box className="box-dashboard">
            {isPending && <LoaderMain tipoLoader="float" tipoImagem="circulo" />}
            <Box className="pad-5 sub-titulo border-radius-8 margin-top-10">
              <Typography
                variant="subtitle2"
                className="font-size-1-1-rem texto-cor-cinza-escuro font-bold pl-2"
              >
                {modalGridLimiteComercial.nomeCliente.toUpperCase()}
                {' - ' + mascararCpfCnpj(modalGridLimiteComercial.cnpjCliente)}
              </Typography>
            </Box>
            <Grid
              className="margin-top-25 borda-bottom-2-main"
              item
              xl={12}
              lg={12}
              md={12}
              sm={12}
              xs={12}
            />
            <Grid item xl={12} lg={12} md={12} sm={12} xs={12} className="margin-top-30">
              {dadosLimitesComerciais.produtos && dadosLimitesComerciais.produtos.length > 0 && (
                <Formik
                  enableReinitialize
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={(values, form) => cadastrarLimiteComercial(values, form)}
                >
                  {props => <InnerForm {...props} produtos={dadosLimitesComerciais.produtos || []} />}
                </Formik>
              )}
              <Box className="grupo-historico">
                <h1>Histórico</h1>
                <Box className="box-historico-scroll">
                  {dadosLimitesComerciais &&
                  dadosLimitesComerciais.historico &&
                  dadosLimitesComerciais.historico.length > 0 ? (
                    dadosLimitesComerciais.historico.map(historico => (
                      <Box
                        className="box-content"
                        key={historico.CODOBSERVACAOLIMITECOMERCIAL + Math.random()}
                      >
                        <Box className="box-historico-usuario">
                          <Box className="produto-valor-box">
                            <p className="produto-valor">
                              {`${historico.NOMEPRODUTO} (${historico.SIGLA}):`}
                              <strong>
                                {Number(historico.VALOR).toLocaleString('pt-BR', {
                                  style: 'currency',
                                  currency: 'BRL'
                                })}
                              </strong>
                            </p>
                          </Box>
                          <span>{historico.NOMEUSUARIO}</span>
                          <span className="data-historico">
                            {new Date(historico.DTHRINSERT).toLocaleDateString('pt-BR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            })}
                          </span>
                          <span className="texto-adicional">às</span>
                          <span className="hora-historico">
                            {new Date(historico.DTHRINSERT).toLocaleTimeString('pt-BR', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </Box>
                        <p>{historico.OBSERVACAO}</p>
                        <div className="divider" />
                      </Box>
                    ))
                  ) : (
                    <Box className="box-content">
                      <p>Nenhum histórico disponível para este cliente.</p>
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>
          </Box>
        ) : (
          ''
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ModalGridDashboardLimiteComercial;
